import type { GroupPhoto } from '@lbc/shared';
import { useEffect, useState } from 'react';

import { api } from '../../services/api/client';

interface PhotoViewerProps {
  photo: GroupPhoto;
  onClose: () => void;
}

export function PhotoViewer({ photo: initialPhoto, onClose }: PhotoViewerProps) {
  const [photo, setPhoto] = useState(initialPhoto);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [likeBusy, setLikeBusy] = useState(false);
  const [likersExpanded, setLikersExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    void api
      .photoContent(initialPhoto.id)
      .then((content) => {
        const nextObjectUrl = URL.createObjectURL(content);
        if (cancelled) {
          URL.revokeObjectURL(nextObjectUrl);
          return;
        }
        objectUrl = nextObjectUrl;
        setImageUrl(nextObjectUrl);
      })
      .catch(() => {
        if (!cancelled) setError('Das Bild konnte gerade nicht geladen werden.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [initialPhoto.id]);

  async function toggleLike() {
    if (likeBusy) return;
    const previous = photo;
    const previousLikersExpanded = likersExpanded;
    const nextLiked = !photo.likedByMe;
    const optimisticLikers = nextLiked ? photo.likers : photo.likers.filter((liker) => !liker.isMe);
    if (optimisticLikers.length === 0) setLikersExpanded(false);
    setPhoto((current) => ({
      ...current,
      likedByMe: nextLiked,
      likeCount: Math.max(0, current.likeCount + (nextLiked ? 1 : -1)),
      likers: optimisticLikers,
    }));
    setLikeBusy(true);
    try {
      const result = await api.togglePhotoLike(photo.id, photo.groupId);
      setPhoto((current) => ({
        ...current,
        likedByMe: result.liked,
        likeCount: result.likeCount,
        likers: result.likers,
        outsideGroupLikeCount: result.outsideGroupLikeCount ?? 0,
        likeGroups: result.likeGroups ?? [],
      }));
      if (result.likers.length === 0) setLikersExpanded(false);
    } catch {
      setPhoto(previous);
      setLikersExpanded(previousLikersExpanded);
      setError('Das Like konnte gerade nicht gespeichert werden.');
    } finally {
      setLikeBusy(false);
    }
  }

  return (
    <div className="photo-viewer-backdrop" role="presentation" onClick={onClose}>
      <section
        className="photo-viewer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="photo-viewer-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="photo-viewer__header">
          <div>
            <p className="step-label">LBC-Moment</p>
            <h2 id="photo-viewer-title">{photo.photographerName}</h2>
            <p>{photo.groupName ?? 'Unbenannte Gruppe'}</p>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label="Bildansicht schließen"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="photo-viewer__image-wrap">
          {loading ? <p className="chart-empty">Bild wird geladen …</p> : null}
          {error !== '' ? (
            <p className="form-error" role="alert">
              {error}
            </p>
          ) : null}
          {imageUrl !== null ? (
            <img
              className="photo-viewer__image"
              src={imageUrl}
              alt={`LBC-Moment von ${photo.photographerName}`}
            />
          ) : null}
        </div>
        <footer className="photo-viewer__footer">
          <button
            className={`photo-heart ${photo.likedByMe ? 'photo-heart--liked' : ''}`}
            type="button"
            aria-label={photo.likedByMe ? 'Like entfernen' : 'Foto liken'}
            aria-pressed={photo.likedByMe}
            disabled={likeBusy}
            onClick={() => void toggleLike()}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z" />
            </svg>
            <span>{photo.likeCount}</span>
          </button>
          <div className="photo-likers" aria-live="polite">
            {photo.outsideGroupLikeCount !== undefined && photo.outsideGroupLikeCount > 0 ? (
              <span className="photo-likers__outside">
                {photo.outsideGroupLikeCount} Likes außerhalb der Gruppe
              </span>
            ) : null}
            {photo.likeCount === 0 ? (
              <span className="photo-likers__empty">Noch keine Likes.</span>
            ) : null}
            {(photo.likeGroups === undefined || photo.likeGroups.length === 0) &&
            photo.likers.length > 0 && !likersExpanded ? (
              <span>
                {photo.likers
                  .slice(0, 2)
                  .map((liker) => liker.displayName)
                  .join(', ')}
                {photo.likers.length > 2 ? (
                  <>
                    {' '}
                    <button
                      className="photo-likers__more"
                      type="button"
                      onClick={() => setLikersExpanded(true)}
                    >
                      und {photo.likers.length - 2} weitere
                    </button>
                  </>
                ) : null}
              </span>
            ) : null}
            {(photo.likeGroups === undefined || photo.likeGroups.length === 0) &&
            likersExpanded && photo.likers.length > 0 ? (
              <div>
                <ul className="photo-likers__list" aria-label="Personen, denen das Foto gefällt">
                  {photo.likers.map((liker, index) => (
                    <li key={`${liker.displayName}-${index}`}>{liker.displayName}</li>
                  ))}
                </ul>
                <button
                  className="photo-likers__more"
                  type="button"
                  onClick={() => setLikersExpanded(false)}
                >
                  Weniger anzeigen
                </button>
              </div>
            ) : null}
            {photo.likeGroups !== undefined && photo.likeGroups.length > 0 ? (
              <div className="photo-likers__groups">
                {photo.likeGroups.map((group) => (
                  <div key={group.groupId}>
                    <strong>{group.groupName ?? 'Unbenannte Gruppe'}</strong>:{' '}
                    {group.likers.map((liker) => liker.displayName).join(', ')}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </footer>
      </section>
    </div>
  );
}
